import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { BlogPost } from '../../lib/blog';
import { AUTHOR_BIO, AUTHOR_NAME } from '../../lib/schema';
import { BlogCard } from './BlogCard';

interface BlogPostProps {
  post: BlogPost;
  relatedPosts?: BlogPost[];
}

export const BlogPostComponent: React.FC<BlogPostProps> = ({ post, relatedPosts = [] }) => {
  const postImage = post.image || "/og-image.png";

  return (
    <article className="prose prose-invert max-w-none">
      {/* Article Header */}
      <header className="mb-8">
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 bg-[#9945FF] text-white text-sm rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
        
        <h1 className="text-4xl font-bold text-white mb-4 leading-tight break-words max-w-full">
          {post.title}
        </h1>
        
        <p className="text-xl text-gray-300 mb-6">
          {post.description}
        </p>
        
        <div className="flex items-center justify-between text-gray-400 mb-6">
          <div className="flex items-center space-x-4">
            <span className="font-medium">{post.author}</span>
            <span>•</span>
            <time dateTime={post.date} className="font-medium">
              {new Date(post.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </time>
            <span>•</span>
            <span>{post.readingTime}</span>
          </div>
        </div>
        
        {post.image && (
          <div className="relative h-64 w-full mb-8 rounded-lg overflow-hidden">
            <Image
              src={postImage}
              alt={post.title}
              fill
              // Without `sizes`, a `fill` image defaults to 100vw and Next
              // serves a 3840px derivative. BlogLayout caps this column at
              // max-w-4xl (896px), so that was ~4x more pixels than any
              // display can use -- on the LCP element of every article.
              sizes="(max-width: 896px) 100vw, 896px"
              className="object-cover"
              priority
            />
          </div>
        )}
      </header>

      {/* Article Content */}
      <div className="prose prose-invert prose-lg max-w-none">
        <div 
          className="blog-content"
          dangerouslySetInnerHTML={{ 
            __html: post.content
              // Process images with clean styling
              // Body images are below the fold in every article, so they are
              // lazy + async-decoded. They still cause a small layout shift on
              // load: a regex cannot read a file's dimensions, so there is no
              // width/height to emit. Fixing that properly means moving body
              // images to next/image via an MDX component map.
              .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<div class="my-8 text-center"><div class="relative w-full max-w-4xl mx-auto"><img src="$2" alt="$1" loading="lazy" decoding="async" class="w-full h-auto rounded-lg shadow-lg" /></div><p class="text-sm text-gray-400 mt-2 italic">$1</p></div>')
              
              // Clean headers - Convert H1 to H2 since page title is already H1
              .replace(/^# (.*$)/gim, '<h2 class="text-3xl font-bold text-white mb-6 mt-12 first:mt-0 border-b border-gray-700 pb-4">$1</h2>')
              .replace(/^## (.*$)/gim, '<h2 class="text-3xl font-bold text-white mb-6 mt-12 first:mt-0">$1</h2>')
              .replace(/^### (.*$)/gim, '<h3 class="text-2xl font-bold text-white mb-4 mt-10 first:mt-0">$1</h3>')
              .replace(/^#### (.*$)/gim, '<h4 class="text-xl font-bold text-white mb-3 mt-8 first:mt-0">$1</h4>')
              .replace(/^##### (.*$)/gim, '<h5 class="text-lg font-bold text-gray-300 mb-2 mt-6 first:mt-0">$1</h5>')
              .replace(/^###### (.*$)/gim, '<h6 class="text-base font-bold text-gray-400 mb-2 mt-4 first:mt-0">$1</h6>')
              
              // Simple text formatting
              .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-white">$1</strong>')
              .replace(/\*(.*?)\*/g, '<em class="italic text-gray-300">$1</em>')
              .replace(/`(.*?)`/g, '<code class="bg-gray-800 text-cyan-400 px-2 py-1 rounded text-sm font-mono">$1</code>')
              // Articles cite off-site sources, so an external link opens in
              // a new tab and carries rel="noopener" against tabnabbing.
              // Deliberately no "nofollow": an outbound link to an
              // authoritative source is a signal worth passing on, not hiding.
              .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, text, href) => {
                const isExternal =
                  /^https?:\/\//.test(href) && !href.startsWith('https://soulminter.io');
                const attrs = isExternal ? ' target="_blank" rel="noopener noreferrer"' : '';
                return `<a href="${href}"${attrs} class="text-purple-400 hover:text-purple-300 underline decoration-purple-400/50 hover:decoration-purple-300 transition-colors">${text}</a>`;
              })
              
              // Clean lists. Each item is tagged with the list type it came
              // from so that runs of them can be wrapped just below. A bare
              // <li> with no <ul>/<ol> parent is invalid markup: the parser
              // hoists it straight out of the surrounding <p>, losing both the
              // bullet and the indent, and it reads as loose text to a crawler.
              .replace(/^[*-] (.*$)/gim, '<li data-list="ul" class="mb-2 text-gray-300">$1</li>')
              .replace(/^\d+\. (.*$)/gim, '<li data-list="ol" class="mb-2 text-gray-300">$1</li>')

              // Wrap each run of consecutive items in a real list element.
              // Tailwind's preflight resets list-style, so the marker has to be
              // reinstated explicitly. <ol> numbers itself, which is why the
              // literal "1. " prefix is dropped above rather than kept as text.
              .replace(/(?:<li data-list="ul"[\s\S]*?<\/li>\n?)+/g, (run) =>
                `<ul class="list-disc">${run.replace(/ data-list="ul"/g, '')}</ul>`)
              .replace(/(?:<li data-list="ol"[\s\S]*?<\/li>\n?)+/g, (run) =>
                `<ol class="list-decimal">${run.replace(/ data-list="ol"/g, '')}</ol>`)
              
              // Paragraphs and spacing
              .replace(/\n\n/g, '</p><p class="mb-6 text-gray-300 leading-relaxed">')
              // Skips lines already opening a heading, a list or any closing
              // tag -- those must not be swallowed into a paragraph.
              .replace(/^(?!<[hlou]|<\/)/gm, '<p class="mb-6 text-gray-300 leading-relaxed">')
              .replace(/<p class="mb-6 text-gray-300 leading-relaxed"><\/p>/g, '')
              
              // Unwrap the <p> the paragraph pass glued around each block:
              // the blank line before a list or heading became an opening <p>,
              // and the one after it a stray </p>.
              .replace(/<p[^>]*>(<(?:ul|ol)[\s\S]*?<\/(?:ul|ol)>)/g, '$1')
              .replace(/(<\/(?:ul|ol)>)<\/p>/g, '$1')
              .replace(/<p[^>]*>(<h[1-6][^>]*>.*?<\/h[1-6]>)/g, '$1')
          }}
        />
      </div>


      {/*
        Frontmatter HowTo, rendered as a visible ordered list. Same contract as
        the FAQ below: BlogSEO turns this exact array into HowTo markup, and
        markup describing steps a reader cannot see is a guidelines violation.
        The body prose already walks through the flow; this is the summary a
        reader (and an answer engine) can lift in one piece.
      */}
      {post.howTo && post.howTo.steps.length > 0 && (
        <section className="mt-16 pt-8 border-t border-gray-700" id="steps">
          <h2 className="text-3xl font-bold text-white mb-2">
            {post.howTo.name}
          </h2>
          <p className="text-gray-400 mb-6">{post.howTo.description}</p>
          <ol className="space-y-4 list-none pl-0">
            {post.howTo.steps.map((step, i) => (
              <li
                key={step.name}
                className="bg-[#192038] rounded-lg p-5 border border-gray-700 flex gap-4"
              >
                <span className="text-[#9945FF] font-mono font-bold shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    {step.name}
                  </h3>
                  <p className="text-gray-300 leading-relaxed m-0">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/*
        Frontmatter FAQ. This renders the same array BlogSEO turns into
        FAQPage markup -- the schema is only allowed to exist because these
        questions are on the page, so do not emit one without the other.
      */}
      {post.faq && post.faq.length > 0 && (
        <section className="mt-16 pt-8 border-t border-gray-700" id="faq">
          <h2 className="text-3xl font-bold text-white mb-6">
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {post.faq.map((item) => (
              <details
                key={item.q}
                className="bg-[#192038] rounded-lg p-5 border border-gray-700 open:border-[#9945FF] transition-colors"
              >
                <summary className="text-lg font-semibold text-white cursor-pointer list-none flex justify-between items-start gap-4">
                  <span>{item.q}</span>
                  <span className="text-[#9945FF] text-xl leading-none shrink-0" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-gray-300 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/*
        Outbound citations. Same contract as the FAQ and HowTo above: BlogSEO
        emits this exact array as `citation` on the BlogPosting, so a reference
        claimed in the markup is always one a reader can see and follow.

        The non-affiliation line is part of the section rather than the prose of
        each article, so linking to a source can never imply a relationship with
        whoever runs it -- and it cannot be forgotten on a future post.
      */}
      {post.sources && post.sources.length > 0 && (
        <section className="mt-16 pt-8 border-t border-gray-700" id="sources">
          <h2 className="text-3xl font-bold text-white mb-6">Sources</h2>
          <ul className="space-y-3 list-none pl-0">
            {post.sources.map((source) => (
              <li key={source.url} className="m-0">
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-purple-300 underline decoration-purple-400/50 hover:decoration-purple-300 transition-colors"
                >
                  {source.name}
                </a>
                <span className="block text-sm text-gray-500 break-all">
                  {source.url}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-gray-500 mt-6 mb-0">
            External links are provided for reference only. SoulMinter is an
            independent software tool and is not affiliated with, endorsed by,
            sponsored by, or partnered with the operators of these sites.
          </p>
        </section>
      )}

      {/*
        Author card. Same contract as the FAQ and HowTo above: BlogSEO emits a
        Person node carrying this exact name and bio, so the byline a reader
        sees and the authorship claim in the markup are one source. The bio
        describes the site operator, so it is shown only for their own posts.
      */}
      {post.author === AUTHOR_NAME && (
        <section className="mt-16 pt-8 border-t border-gray-700" id="author">
          <h2 className="text-xl font-bold text-white mb-4">About the author</h2>
          <div className="bg-[#192038] rounded-lg p-5 border border-gray-700">
            <p className="text-lg font-semibold text-white m-0">{post.author}</p>
            <p className="text-gray-300 leading-relaxed mt-1 mb-0">
              {AUTHOR_BIO}
            </p>
            <Link
              href="/about"
              className="inline-block mt-3 text-purple-400 hover:text-purple-300 underline decoration-purple-400/50 hover:decoration-purple-300 transition-colors"
            >
              Operator details
            </Link>
          </div>
        </section>
      )}

      {/* Social Share */}
      <div className="mt-12 pt-8 border-t border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">Share this article</h3>
        <div className="flex space-x-4">
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://soulminter.io/blog/${post.slug}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-[#1DA1F2] text-white share-btn rounded-lg hover:bg-[#1a8cd8] transition-colors no-underline"
          >
            Share on Twitter
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://soulminter.io/blog/${post.slug}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-[#0077B5] text-white share-btn rounded-lg hover:bg-[#005885] transition-colors no-underline"
          >
            Share on LinkedIn
          </a>
        </div>
      </div>

      {/* Call to Action */}
      <div className="mt-12 p-6 bg-gradient-to-r from-[#9945FF] to-[#00C2FF] rounded-lg text-center">
        <h3 className="text-2xl font-bold text-white mb-4">
          Ready to Create Your Own Solana Token?
        </h3>
        <p className="text-white/90 mb-6">
          Put your knowledge into practice with SoulMinter&apos;s no-code token creation platform.
        </p>
        <Link
          href="https://soulminter.io/create"
          className="inline-block px-8 py-3 bg-white text-[#9945FF] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
        >
          Create Token Now
        </Link>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-white mb-8">Related Articles</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedPosts.map((relatedPost) => (
              <BlogCard key={relatedPost.slug} post={relatedPost} />
            ))}
          </div>
        </div>
      )}
    </article>
  );
};
